import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  TagsHistogramEntry,
  TagsHistogramInterval,
} from '../../../api/hexa/models/EntityStatisticsModels';

interface TagsHistogramParams {
  start_date?: string;
  end_date?: string;
  interval: TagsHistogramInterval;
}

interface TagsHistogramFetchResult {
  interval: TagsHistogramInterval;
  histogram: TagsHistogramEntry[];
}

const defaultStartDate = () => dayjs().subtract(1, 'month').toISOString();
const defaultEndDate = () => dayjs().toISOString();

export const useTagsHistogramStats = (
  fetchResults: (
    params: TagsHistogramParams
  ) => Promise<TagsHistogramFetchResult>,
  onError: (message: string) => void
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [histogram, setHistogram] = useState<TagsHistogramEntry[]>([]);
  const [intervalUnit, setIntervalUnit] =
    useState<TagsHistogramInterval>('day');
  const [startDate, setStartDate] = useState<string | null>(
    defaultStartDate()
  );
  const [endDate, setEndDate] = useState<string | null>(defaultEndDate());
  // Empty means the most searched tags are plotted by default
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  /**
   * Rapid filter changes can leave several requests in flight, so only
   * the most recent one is allowed to update state
   */
  const latestRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    const isLatestRequest = () => requestId === latestRequestId.current;
    try {
      setIsLoading(true);
      const response = await fetchResults({
        start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
        end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        interval: intervalUnit,
      });
      if (!isLatestRequest()) return;
      setHistogram(response.histogram);
    } catch (error: any) {
      if (!isLatestRequest()) return;
      onError(
        `Error ${error.status ?? 500} fetching statistics:
          ${error.response?.data.error ?? 'Error fetching statistics'}`
      );
    } finally {
      if (isLatestRequest()) setIsLoading(false);
    }
  }, [fetchResults, startDate, endDate, intervalUnit, onError]);

  useEffect(() => {
    fetchData();
    const requestIdRef = latestRequestId;
    return () => {
      // Invalidate the pending request so it can't update state after unmount
      requestIdRef.current++;
    };
  }, [fetchData]);

  const resetFilters = useCallback(() => {
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
    setIntervalUnit('day');
    setSelectedTags([]);
  }, []);

  return {
    isLoading,
    histogram,
    intervalUnit,
    setIntervalUnit,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedTags,
    setSelectedTags,
    resetFilters,
  };
};
